import { FC, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import type { GridFilterItem, GridLogicOperator } from "@mui/x-data-grid";

import { GENDER_OPTIONS, GENDER_PREFERENCES, LANGUAGES, SERVICE_OPTIONS } from "~/utils/providerRegistrationHelpers";

type ColumnType = "text" | "number" | "multiselect";

interface FilterColumn {
  field: string;
  label: string;
  type: ColumnType;
  options?: { key: string; label: string }[];
}

const FILTER_COLUMNS: FilterColumn[] = [
  { field: "name", label: "Name", type: "text" },
  {
    field: "services",
    label: "Services",
    type: "multiselect",
    options: SERVICE_OPTIONS.map(([key, label]) => ({ key, label })),
  },
  { field: "distance", label: "Distance (Miles)", type: "number" },
  {
    field: "languages",
    label: "Language(s)",
    type: "multiselect",
    options: LANGUAGES.map(({ code, name }) => ({ key: code, label: name })),
  },
  {
    field: "genderDisclosure",
    label: "Gender",
    type: "multiselect",
    options: GENDER_OPTIONS.map(([key, label]) => ({ key, label })),
  },
  {
    field: "genderPreference",
    label: "Employer Gender Preference",
    type: "multiselect",
    options: GENDER_PREFERENCES.map(([key, label]) => ({ key, label })),
  },
];

const TEXT_OPERATORS = [
  { value: "contains", label: "contains" },
  { value: "equals", label: "equals" },
  { value: "startsWith", label: "starts with" },
  { value: "endsWith", label: "ends with" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];

const NUMBER_OPERATORS = [
  { value: "=", label: "=" },
  { value: "!=", label: "≠" },
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
];

const MULTISELECT_OPERATORS = [{ value: "includesAnyOf", label: "includes any of" }];

const getOperatorsForColumn = (col: FilterColumn | undefined) => {
  if (!col) return TEXT_OPERATORS;
  if (col.type === "number") return NUMBER_OPERATORS;
  if (col.type === "multiselect") return MULTISELECT_OPERATORS;
  return TEXT_OPERATORS;
};

const defaultOperatorForColumn = (col: FilterColumn | undefined): string => {
  const ops = getOperatorsForColumn(col);
  return ops[0].value;
};

const makeDefaultFilterItem = (): GridFilterItem => {
  const defaultCol = FILTER_COLUMNS[0];
  return {
    id: Date.now(),
    field: defaultCol.field,
    operator: defaultOperatorForColumn(defaultCol),
    value: undefined,
  };
};
const DebouncedTextInput: FC<{
  value: any;
  onChange: (val: any) => void;
  type?: "text" | "number";
}> = ({ value, onChange, type = "text" }) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(e.target.value), 300);
  };

  return (
    <TextField
      label="Value"
      onChange={handleChange}
      size="small"
      sx={type === "number" ? { width: 120 } : { minWidth: 160 }}
      type={type}
      value={localValue}
    />
  );
};

interface ValueInputProps {
  column: FilterColumn | undefined;
  operator: string;
  value: any;
  onChange: (val: any) => void;
}

const ValueInput: FC<ValueInputProps> = ({ column, operator, value, onChange }) => {
  if (!column || operator === "isEmpty" || operator === "isNotEmpty") return null;

  if (column.type === "multiselect" && column.options) {
    const selected = (value ?? []) as string[];
    const selectedOptions = column.options.filter((o) => selected.includes(o.key));

    return (
      <Autocomplete
        filterSelectedOptions={false}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.key === v.key}
        onChange={(_e, newValue) => onChange(newValue.map((o) => o.key))}
        options={column.options}
        renderInput={(params) => (
          <TextField {...params} label="Values" placeholder={selectedOptions.length === 0 ? "Search..." : undefined} size="small" />
        )}
        renderOption={(props, option, { selected: isSelected }) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              <Checkbox
                checked={isSelected}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              {option.label}
            </li>
          );
        }}
        renderValue={(val, getTagProps) =>
          val.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip key={key} label={option.label} size="small" {...tagProps} />;
          })
        }
        size="small"
        slotProps={{
          listbox: { sx: { maxHeight: 250 } },
          popper: { placement: "bottom-start" },
        }}
        sx={{ minWidth: 220 }}
        value={selectedOptions}
        disableCloseOnSelect
        multiple
      />
    );
  }

  return (
    <DebouncedTextInput
      onChange={onChange}
      value={value}
      {...(column.type === "number" && {
        type: "number",
      })}
    />
  );
};

interface TableFilterPanelProps {
  filterItems: GridFilterItem[];
  logicOperator: GridLogicOperator;
  onFilterItemsChange: (items: GridFilterItem[]) => void;
  onLogicOperatorChange: (op: GridLogicOperator) => void;
}

const TableFilterPanel: FC<TableFilterPanelProps> = ({ filterItems, logicOperator, onFilterItemsChange, onLogicOperatorChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const activeCount = filterItems.filter((item) => item.value !== undefined && item.value !== "" && item.value?.length !== 0).length;
  const isAddRedundant =
    filterItems.length === 1 && (filterItems[0].value === undefined || filterItems[0].value === "" || filterItems[0].value?.length === 0);

  useEffect(() => {
    if (filterItems.length === 0) {
      onFilterItemsChange([makeDefaultFilterItem()]);
    }
  }, [filterItems.length, onFilterItemsChange]);

  const handleAdd = () => {
    onFilterItemsChange([...filterItems, makeDefaultFilterItem()]);
  };

  const handleRemove = (id: GridFilterItem["id"]) => {
    onFilterItemsChange(filterItems.filter((item) => item.id !== id));
  };

  const handleChange = (id: GridFilterItem["id"], patch: Partial<GridFilterItem>) => {
    onFilterItemsChange(
      filterItems.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, ...patch };
        if (patch.field) {
          const col = FILTER_COLUMNS.find((c) => c.field === patch.field);
          updated.operator = defaultOperatorForColumn(col);
          updated.value = undefined;
        }
        return updated;
      }),
    );
  };

  return (
    <>
      <Button
        endIcon={
          <Badge
            badgeContent={activeCount}
            color="primary"
            invisible={activeCount === 0}
            sx={{
              "& .MuiBadge-badge": {
                right: -2,
                top: 2,
                height: 16,
                minWidth: 16,
              },
            }}
          >
            <FilterListIcon fontSize="small" />
          </Badge>
        }
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        sx={{ borderRadius: 999, textTransform: "none" }}
        variant="outlined"
      >
        Filters
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => setAnchorEl(null)}
        open={open}
        slotProps={{ paper: { sx: { p: 2, minWidth: 500, maxWidth: 700 } } }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography fontWeight="bold" variant="body2">
            Filters
          </Typography>
          {filterItems.length > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ToggleButtonGroup onChange={(_e, val) => val && onLogicOperatorChange(val)} size="small" value={logicOperator} exclusive>
                <ToggleButton sx={{ px: 2, textTransform: "none" }} value="and">
                  AND
                </ToggleButton>
                <ToggleButton sx={{ px: 2, textTransform: "none" }} value="or">
                  OR
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton onClick={() => onFilterItemsChange([])} size="small" title="Clear all filters">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
        {filterItems.length > 0 && <Divider sx={{ mb: 1.5 }} />}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {filterItems.map((item, index) => {
            const col = FILTER_COLUMNS.find((c) => c.field === item.field);
            const operators = getOperatorsForColumn(col);

            return (
              <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {filterItems.length > 1 && (
                  <Typography color="text.secondary" sx={{ width: 32, textAlign: "right", flexShrink: 0 }} variant="caption">
                    {index === 0 ? "Where" : logicOperator.toUpperCase()}
                  </Typography>
                )}
                <Select
                  onChange={(e) => handleChange(item.id, { field: e.target.value })}
                  size="small"
                  sx={{ minWidth: 160 }}
                  value={item.field}
                >
                  {FILTER_COLUMNS.map((c) => (
                    <MenuItem key={c.field} value={c.field}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  onChange={(e) => handleChange(item.id, { operator: e.target.value, value: undefined })}
                  size="small"
                  sx={{ minWidth: 120 }}
                  value={item.operator}
                >
                  {operators.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
                <ValueInput
                  column={col}
                  onChange={(val) => handleChange(item.id, { value: val })}
                  operator={item.operator}
                  value={item.value}
                />
                <IconButton onClick={() => handleRemove(item.id)} size="small" sx={{ ml: "auto", flexShrink: 0 }} title="Delete filter">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: 2 }}>
          {!isAddRedundant && (
            <Button onClick={handleAdd} size="small" startIcon={<AddIcon />} variant="text">
              Add filter
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default TableFilterPanel;
